import { CalendarClock, Clock, Radar, Receipt, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { Empty } from "../components/ui";
import { roleList, type AppId, type Me } from "../lib/api";
import { activeOption, useConnections } from "../lib/connections";

const ICONS: Record<AppId, typeof Workflow> = { ja: Workflow, expense: Receipt, time: Clock, payables: CalendarClock, radar: Radar };

/** App tiles for the apps this user is entitled to (design §7). */
export default function Launcher({ me }: { me: Me }) {
  const apps = me.apps ?? [];
  const conns = useConnections();
  return (
    <div>
      <h1 className="text-2xl font-bold text-ink mb-1">Welcome{me.name ? `, ${me.name.split(" ")[0]}` : ""}</h1>
      <p className="text-sm text-muted mb-6">Your Intellias apps for {me.tenant!.name}.</p>
      {apps.length === 0 ? (
        <Empty icon={<Workflow className="size-8" />} title="No apps yet">
          {me.tenantAdmin ? "Give yourself or your team an app on the Users page." : "Your administrator hasn't given you any apps yet."}
        </Empty>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map((a) => {
            const Icon = ICONS[a.id];
            return (
              <Link key={a.id} to={`/apps/${a.id}`} className="group rounded-2xl border border-line bg-surface p-5 hover:border-accent transition-colors">
                <div className="size-11 rounded-xl brand-bar text-white inline-flex items-center justify-center mb-4">
                  <Icon className="size-5" aria-hidden />
                </div>
                <div className="font-display font-semibold text-ink group-hover:text-accent-text">{a.name}</div>
                <div className="text-xs text-muted mt-1">{a.roles[0] === "user" ? "Access" : roleList(a.roles)}</div>
                <ConnLine app={a.id} conns={conns} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConnLine({ app, conns }: { app: AppId; conns: ReturnType<typeof useConnections> }) {
  if (!conns.loaded) return null;
  const c = conns.apps[app];
  const active = activeOption(c);
  if (active) return <div className={`text-xs mt-2 ${active.production ? "text-bad font-medium" : "text-ink-2"}`}>{active.name} · {active.envLabel}</div>;
  const usable = c?.options.some((o) => o.usable);
  return <div className="text-xs mt-2 text-warn">{usable ? "Choose a connection inside the app" : "No connection available yet"}</div>;
}

